import numeral from 'numeral';

/**
 * Formats a number for display in stats tables and summaries.
 * Returns em dash '—' for null/undefined values.
 * Uses comma formatting for numbers under 1000, abbreviated format for larger numbers.
 */
export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return numeral(value).format('0,0');
};

/**
 * Formats a USD amount for display (e.g. "$8.00").
 * Returns em dash '—' for null/undefined values.
 */
export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return numeral(value).format('$0,0.00');
};

/**
 * Formats a percentage for display.
 * Returns em dash '—' for null/undefined values.
 */
export const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return numeral(value).format('0.0%');
};

/**
 * Formats a duration in milliseconds as human-readable seconds (e.g., "2.3s").
 * Returns em dash '—' for null/undefined/zero values.
 */
export const formatDuration = (valueMs: number | null | undefined): string => {
  if (valueMs === null || valueMs === undefined || valueMs === 0) {
    return '—';
  }
  const seconds = valueMs / 1000;
  return `${numeral(seconds).format('0.0')}s`;
};

/**
 * Formats a number, percentage, or currency amount for stats summaries.
 * Uses abbreviated format for large numbers (1.5M, 2.3k).
 * Returns em dash '—' for null/undefined values.
 */
export const formatStatValue = (
  value: number | null | undefined,
  format: 'number' | 'percentage' | 'currency' = 'number'
): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  if (format === 'percentage') {
    return numeral(value).format('0.0%');
  }
  if (format === 'currency') {
    if (value >= 1000000) {
      return numeral(value).format('$0.00a').toUpperCase();
    }
    if (value >= 1000) {
      return numeral(value).format('$0.0a');
    }
    return numeral(value).format('$0,0.00');
  }
  // Use abbreviated format for large numbers
  if (value >= 1000000) {
    return numeral(value).format('0.00a').toUpperCase();
  }
  if (value >= 1000) {
    return numeral(value).format('0.0a');
  }
  return numeral(value).format('0,0');
};
